import { useCallback, useContext, useEffect, useRef } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { SocketContext } from "../../../SocketConnection";
import { useAppDispatch, useSelector } from "../../../redux-store";
import { CargoData } from "../../../ducks/app/cargo-dialog/types";
import { mergeRemoteChanges } from "./merge-remote-changes";
import CreateShipmentData from "./CreateEditShipmentData";
import { FormFieldSetters, formFieldSetters } from "./form-field-setters";
import {
  loadedRevisionOf,
  OpenCargoSyncSource,
} from "./open-cargo-sync-sources";

type Outcome = "merged" | "conflict" | "nothing";

/**
 * Keeps the open cargo dialog on the cargo's latest revision (DORA-441).
 *
 * The dialog joins the cargo's socket room. Whenever the server says the cargo
 * changed, the latest version is fetched and merged into the form: someone
 * else's changes to fields the user didn't touch are taken in and the dialog
 * moves to the new revision, so its next save goes through. A change that
 * clashes with the user's unsaved edits flags a conflict instead, and the
 * dialog takes in nothing more.
 *
 * `cargoId` is null when there is nothing to keep in sync (a new cargo or
 * template, or the feature is off). A cargo template is a cargo row of its
 * own: `source` says where its revision lives and how to fetch it.
 */
export const useOpenCargoSync = (input: {
  cargoId: string | null;
  source: OpenCargoSyncSource;
  formMethods: UseFormReturn<CreateShipmentData>;
  // Maps loaded cargo data to form values, the way the form was initialised.
  toFormValues: (data: CargoData) => CreateShipmentData;
  isSaving: boolean;
}) => {
  const { cargoId, source, formMethods, toFormValues, isSaving } = input;
  const socket = useContext(SocketContext);
  const dispatch = useAppDispatch();

  // Read inside async callbacks, which must see the current values.
  const conflict = useRef(false);
  conflict.current = useSelector(source.selectRemoteConflict);
  const saving = useRef(isSaving);
  saving.current = isSaving;
  const toFormValuesRef = useRef(toFormValues);
  toFormValuesRef.current = toFormValues;

  // A sync asked for while a save or another sync is running runs after it.
  const running = useRef(false);
  const pending = useRef(false);

  const sync = useCallback(
    async (options: { duringSave?: boolean } = {}): Promise<Outcome> => {
      if (!cargoId || conflict.current) {
        return conflict.current ? "conflict" : "nothing";
      }
      if (running.current || (saving.current && !options.duringSave)) {
        pending.current = true;
        return "nothing";
      }
      running.current = true;
      try {
        const latest = await source.fetchLatest(dispatch, cargoId);
        // Deleted meanwhile (the dialog now shows that), or nothing new.
        if (
          !latest ||
          latest.revision === dispatch(loadedRevisionOf(source))
        ) {
          return "nothing";
        }
        const { getValues, reset, setValue, control } = formMethods;
        const theirs = toFormValuesRef.current(latest.data);
        // What the form last started from: the load, the last save (Ctrl+S
        // resets the form to it) or the last merge. Read from the control:
        // formState.defaultValues is a snapshot from the last render, older
        // than a reset that hasn't rendered yet.
        const base: FieldValues = control._defaultValues;
        const result = mergeRemoteChanges({
          base,
          mine: getValues(),
          theirs,
        });
        if (result.kind === "conflict") {
          dispatch(source.remoteConflict());
          return "conflict";
        }
        // The latest version (with the form's own flags) becomes what the
        // form starts from, then the user's own changes go back on top, still
        // marked as unsaved. Nothing else is: closing the dialog asks about
        // unsaved changes.
        reset(result.start);
        const setters: FormFieldSetters = formFieldSetters(setValue);
        const putBack = <K extends keyof CreateShipmentData>(field: K) =>
          setters[field]?.(result.values[field]);
        result.differFromStart.forEach(putBack);
        dispatch(source.revisionAdopted(latest.revision));
        return "merged";
      } catch {
        // The fetch thunk has already reported it; the next nudge, or the
        // save, tries again.
        return "nothing";
      } finally {
        running.current = false;
        if (pending.current && !saving.current) {
          pending.current = false;
          void syncRef.current();
        }
      }
    },
    [cargoId, source, dispatch, formMethods]
  );
  const syncRef = useRef(sync);
  syncRef.current = sync;

  // Run what was asked for while busy, once the save is done.
  useEffect(() => {
    if (!isSaving && pending.current) {
      pending.current = false;
      void sync();
    }
  }, [isSaving, sync]);

  useEffect(() => {
    if (!socket || !cargoId) {
      return;
    }
    const join = () => socket.emit("join-cargo-room", { cargoId });
    const onChanged = (event: { cargoId: string; revision: string }) => {
      if (
        event.cargoId === cargoId &&
        event.revision !== dispatch(loadedRevisionOf(source))
      ) {
        void sync();
      }
    };
    // A reconnect gets a new socket on the server, without its rooms, and may
    // have missed nudges.
    const onReconnect = () => {
      join();
      void sync();
    };
    join();
    socket.on("cargo.revision-changed", onChanged);
    socket.on("connect", onReconnect);
    return () => {
      socket.off("cargo.revision-changed", onChanged);
      socket.off("connect", onReconnect);
      socket.emit("leave-cargo-room", { cargoId });
    };
  }, [socket, cargoId, sync, dispatch, source]);

  // A save came back "outdated revision": take in what changed and say
  // whether the save can simply be sent again.
  const mergeAfterOutdatedSave = useCallback(
    async () => (await sync({ duringSave: true })) === "merged",
    [sync]
  );

  // Take in whatever changed since the revision the dialog is on.
  const catchUp = useCallback(() => {
    void sync();
  }, [sync]);

  return { mergeAfterOutdatedSave, catchUp };
};
